import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import { flatHookFormErrors } from 'controllers/TaskManager/controllers/utils'
import { useEffect, useState } from 'react'
import { useReload } from 'store'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import { useAnalytics } from './useAnalytics'
import AmplitudeTaskParameters from 'models/Amplitude/AmplitudeTaskParameters'
import { useRecoilValue } from 'recoil'
import { openedTask } from 'store'

const tasksAmplitudeActions = {
	request_approval: ['sendToMedic'],
	request_manager_approval: ['sendToManager'],
	resolve: ['resolveTask', 'finishTask'],
	finalize: ['approveTask'],
	refuse: ['refuseTask', 'finishTask'],
	postpone: ['postponeTask'],
	submit: ['submitTask', 'finishTask'],
	nullify: ['nullifyTask', 'finishTask'],
}

export const translateErrorMessage = (error, i18n) => {
	const ErrorMessages = {
		LEGACY_PORTAL_CONNECTION_FAILED: i18n`The Portal failed to respond`,
		GRPC_CONNECTION_FAILED: i18n`Grpc service failed to respond`,
	}

	const message =
		error.message || error.error || i18n`An error occur. Try again later.`

	return ErrorMessages[message] ? ErrorMessages[message] : message
}

const useSubmitTask = (taskId, formStateErrors) => {
	const [error, setError] = useState(null)
	const [submitting, setSubmitting] = useState(null)
	const reload = useReload()
	const { i18n, expireSession } = useGlobal()
	const { dispatchAnalyticsEvent } = useAnalytics()
	const currentOpenedTask = useRecoilValue(openedTask)

	useEffect(() => {
		const flattenErrors = flatHookFormErrors(formStateErrors)

		if (flattenErrors) {
			const errorMessages = Object.keys(flattenErrors).map((errorKey) => {
				const errorMessage = flattenErrors[errorKey]
				const splittedErrorKey = errorKey.split('.')
				const field = splittedErrorKey[splittedErrorKey.length - 2]

				return `${i18n.translate(field)}: ${i18n.translate(
					errorMessage
				)}`
			})
			setError(errorMessages)
		}
	}, [formStateErrors, i18n])

	const requestApprovalTask = (bodyData) => {
		return sendTask('request_approval', bodyData, 'request_approval')
	}

	const requestManagerApprovalTask = (bodyData) => {
		return sendTask(
			'request_manager_approval',
			bodyData,
			'request_manager_approval'
		)
	}

	const resolveTask = (bodyData, action) => {
		return sendTask('resolve', bodyData, action || 'resolve')
	}

	const approveTask = (bodyData, action) => {
		return sendTask('finalize', bodyData, action || 'finalize')
	}

	const refuseTask = (bodyData) => {
		return sendTask('refuse', bodyData, 'refuse')
	}

	const refuseRequestTask = (bodyData) => {
		return sendTask('refuse_request', bodyData, 'refuse')
	}

	const postponeTask = (bodyData) => {
		return sendTask('postpone', bodyData, 'postpone')
	}

	const sendTask = (endpointSuffix, bodyData, amplitudeAction) => {
		if (submitting) {
			return
		}

		setError(null)
		setSubmitting('submitting')

		return fetch(
			`${API_URL}/api/tasks/${taskId}/${endpointSuffix}`,
			fetchOptions({
				method: 'PUT',
				body: bodyData,
			})
		)
			.then(async (r) => {
				if (r.ok) {
					setSubmitting('success')
					reload([
						'/tasks/me?state=Created',
						'/tasks/me?state=Postponed',
						'/tasks/stats',
					])

					tasksAmplitudeActions[amplitudeAction]?.map((event) =>
						dispatchAnalyticsEvent(
							event,
							AmplitudeTaskParameters.fromJSON(
								currentOpenedTask.openTask
							)
						)
					)
				} else {
					setError([(await parseJSONContent(r)).message])
				}
			})
			.catch((e) => {
				if (e.status === '401' || e.status === 401) {
					expireSession()
				}

				const errorMessage = translateErrorMessage(e, i18n)

				setSubmitting(false)
				setError([errorMessage])
				throw e
			})
	}

	return {
		error,
		setError,
		submitting,
		requestManagerApprovalTask,
		requestApprovalTask,
		approveTask,
		resolveTask,
		refuseTask,
		refuseRequestTask,
		postponeTask,
	}
}

export default useSubmitTask
